import { useCallback, useState } from 'react'
import { crmLeadContractApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import { mapSigningStateToContractStatus } from '@pages/Admin/Reports/ContractReport/utils/contractSigningState.utils'
import type {
  ContractLoadingState,
  LeadContractDock,
} from './leadContracts.types'

export const useLeadContractActions = (
  leadId: number,
  onContractChanged: () => void | Promise<void>,
) => {
  const addAlert = useAddAlert()
  const [contractLoading, setContractLoading] = useState<ContractLoadingState>(
    null,
  )

  const handleContractEndDateChange = useCallback(
    async (contract: LeadContractDock, date: string | null) => {
      setContractLoading({ id: contract.id, field: 'endDate' })
      try {
        const res = await crmLeadContractApi.update(
          { lead_id: leadId, id: contract.id },
          { contract_end_date: date },
        )
        if (isError(res)) throw new Error()
        await onContractChanged()
      } catch {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Не удалось обновить дату завершения договора',
        })
      } finally {
        setContractLoading(null)
      }
    },
    [addAlert, leadId, onContractChanged],
  )

  const handleContractToggleProcessed = useCallback(
    async (contract: LeadContractDock) => {
      setContractLoading({ id: contract.id, field: 'processed' })
      try {
        const res = await crmLeadContractApi.toggleProcessed({
          lead_id: leadId,
          id: contract.id,
        })
        if (isError(res)) throw new Error()
        await onContractChanged()
      } catch {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Не удалось изменить статус обработки договора',
        })
      } finally {
        setContractLoading(null)
      }
    },
    [addAlert, leadId, onContractChanged],
  )

  const handleContractSigningStateChange = useCallback(
    async (contract: LeadContractDock, signingState: string) => {
      setContractLoading({ id: contract.id, field: 'signingState' })
      try {
        const res = await crmLeadContractApi.update(
          { lead_id: leadId, id: contract.id },
          {
            contract_status: mapSigningStateToContractStatus(signingState),
          },
        )
        if (isError(res)) throw new Error()
        await onContractChanged()
      } catch {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Не удалось обновить статус подписания',
        })
      } finally {
        setContractLoading(null)
      }
    },
    [addAlert, leadId, onContractChanged],
  )

  const handleDeleteContract = useCallback(
    async (contractId: number) => {
      try {
        const res = await crmLeadContractApi.delete({
          lead_id: leadId,
          id: contractId,
        })
        if (isError(res)) throw new Error()
        await onContractChanged()
        addAlert({
          type: 'success',
          title: 'Успешно',
          text: 'Договор удалён',
        })
        return true
      } catch {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: 'Не удалось удалить договор',
        })
        return false
      }
    },
    [addAlert, leadId, onContractChanged],
  )

  return {
    contractLoading,
    handleContractEndDateChange,
    handleContractToggleProcessed,
    handleContractSigningStateChange,
    handleDeleteContract,
  }
}
